import { Box, Button, TextField, Typography, Grid } from '@mui/material';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import React, { CSSProperties, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import RequiredLabel from '../UtilityComponent/RequiredLabel';
import './RequestersInformation.css'; // Import CSS for styling
import { getRequesterInformation, clearRequestorInformation } from '../../store/formSlice';
import { formatPhoneNumber } from '../UtilityComponent/validationUtils';
import { getUserEmail } from '../../store/OktaAuthSlice';

interface ReceivingInformationProps {
  onNext: () => void;
  onBack: () => void;
  values: {
    requesterName: string;
    requesterEmail: string;
    requesterPhoneNumber: string;
    alternateName: string;
    alternateEmail: string;
    alternatePhoneNumber: string;
    alternateContactType: string;
    requesterAlternateContacts: Array<{
      contactName: string;
      contactEmail: string;
      contactPhoneNumber: string;
    }>;
  };
  errors: any;
  touched: any;
  alternateContactType: string;
}

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '100%',
    margin: 'auto',
    marginTop: '10px',
    marginLeft: '10%',
  },
  title: {
    color: 'blue',
    fontSize: '1rem',
  },
  inputContainer: {
    flex: 1,
    marginRight: '10px',
    display: 'flex',
    flexDirection: 'column',
  },
};

const RequestersInformation: React.FC<ReceivingInformationProps> = ({
  onNext,
  onBack,
  values,
  errors,
  touched,
  alternateContactType,
}) => {
  const {
    requesterName,
    requesterEmail,
    requesterPhoneNumber,
    alternateName,
    alternateEmail,
    alternatePhoneNumber,
    requesterAlternateContacts,
  } = useSelector(getRequesterInformation);
  const dispatch = useDispatch();
  const { setFieldValue, setErrors } = useFormikContext<any>();
  const email = useSelector(getUserEmail);

  useEffect(() => {
    dispatch(clearRequestorInformation());
    if (requesterName && !values.requesterName) {
      setFieldValue('requesterName', requesterName);
    }
    if (requesterEmail && !values.requesterEmail) {
      setFieldValue('requesterEmail', requesterEmail);
    }
    if (requesterPhoneNumber && !values.requesterPhoneNumber) {
      setFieldValue('requesterPhoneNumber', requesterPhoneNumber);
    }
    if (requesterAlternateContacts?.length > 0) {
      requesterAlternateContacts.forEach((contact: { contactName: string; contactEmail: string; contactPhoneNumber: string }) => {
        if (contact.contactName && !values.alternateName) {
          setFieldValue('alternateName', contact.contactName);
        }
        if (contact.contactEmail && !values.alternateEmail) {
          setFieldValue('alternateEmail', contact.contactEmail);
        }
        if (contact.contactPhoneNumber && !values.alternatePhoneNumber) {
          setFieldValue('alternatePhoneNumber', contact.contactPhoneNumber);
        }
      });
    }
  }, [requesterName, requesterEmail, requesterPhoneNumber, requesterAlternateContacts, setFieldValue, values]);

  useEffect(() => {
    if (email) {
      setFieldValue('requesterEmail', email);
    }
  }, [email, setFieldValue]);

  const handleBack = () => {
    setErrors({});
    onBack();
  };

  const handleFormValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'requesterPhoneNumber' || name === 'alternatePhoneNumber') {
      const formattedPhoneNumber = formatPhoneNumber(value, 'US');
      setFieldValue(name, formattedPhoneNumber);
    } else {
      setFieldValue(name, value);
    }
  };

  return (
    <div style={{ height: 'auto', overflowY: 'auto' }}>
    <div style={{ ...styles.container, paddingBottom: '200px' /* Increased padding to ensure last field is visible */ }}>
      <h2 style={styles.title}>Requester Information</h2>
      <Grid container spacing={2} alignItems="center">
  {/* Requester's Name */}
  <Grid item xs={12} sm={4} md={3}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Requester's Name" mandatory={true} />
      <TextField
        id="requesterName"
        name="requesterName"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.requesterName}
        placeholder="Please enter the requester's name"
        onChange={handleFormValueChange}
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="requesterName" component="div" className="error-message" />
      </div>
    </div>
  </Grid>

  {/* Requester's Email */}
  <Grid item xs={12} sm={4} md={4}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Email" mandatory={true} />
      <TextField
        id="requesterEmail"
        name="requesterEmail"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.requesterEmail}
        placeholder="Please enter the email address"
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="requesterEmail" component="div" className="error-message" />
      </div>
    </div>
  </Grid>

  {/* Divider */}
  <Grid item xs={12} sm={2} md={1} container justifyContent="center" alignItems="center">
    <Typography
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        color: '#616161',
        marginTop: '0px',
      }}
    >
      <span style={{ margin: '0 4px' }}>•••••</span>or<span style={{ margin: '0 4px' }}>•••••</span>
    </Typography>
  </Grid>

  {/* Requester's Phone Number */}
  <Grid item xs={12} sm={4} md={3}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Phone Number" />
      <TextField
        id="requesterPhoneNumber"
        name="requesterPhoneNumber"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.requesterPhoneNumber}
        placeholder="Please enter the phone number"
        onChange={handleFormValueChange}
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="requesterPhoneNumber" component="div" className="error-message" />
      </div>
    </div>
  </Grid>
</Grid>

<h2 style={styles.title}>Alternate Contact</h2>
<Grid container spacing={2} alignItems="center">
  {/* Alternate Name */}
  <Grid item xs={12} sm={4} md={3}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Name" />
      <TextField
        id="alternateName"
        name="alternateName"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.alternateName}
        placeholder="Please enter the alternate name"
        onChange={handleFormValueChange}
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="alternateName" component="div" className="error-message" />
      </div>
    </div>
  </Grid>

  {/* Alternate Email */}
  <Grid item xs={12} sm={4} md={4}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Email" />
      <TextField
        id="alternateEmail"
        name="alternateEmail"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.alternateEmail}
        placeholder="Please enter the email address"
        onChange={handleFormValueChange}
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="alternateEmail" component="div" className="error-message" />
      </div>
    </div>
  </Grid>

  {/* Divider */}
  <Grid item xs={12} sm={2} md={1} container justifyContent="center" alignItems="center">
    <Typography
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        color: '#616161',
        marginTop: '0px',
      }}
    >
      <span style={{ margin: '0 4px' }}>•••••</span>or<span style={{ margin: '0 4px' }}>•••••</span>
    </Typography>
  </Grid>

  {/* Alternate Phone Number */}
  <Grid item xs={12} sm={4} md={3}>
    <div style={styles.inputContainer}>
      <RequiredLabel label="Phone Number" />
      <TextField
        id="alternatePhoneNumber"
        name="alternatePhoneNumber"
        type="text"
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        InputProps={{
          style: {
            fontSize: '0.8rem',
          },
        }}
        autoComplete="off"
        value={values.alternatePhoneNumber}
        placeholder="Please enter the phone number"
        onChange={handleFormValueChange}
      />
      <div style={{ minHeight: '1.5rem' /* Reserve space for error messages */ }}>
        <ErrorMessage name="alternatePhoneNumber" component="div" className="error-message" />
      </div>
    </div>
  </Grid>
</Grid>
  
      <Field name="alternateContactType" type="hidden" value={alternateContactType} />
  
      {/* Footer */}
      <Box
  sx={{
    position: 'fixed', // Keep the buttons fixed at the bottom
    bottom: '60px', // Distance from the bottom of the screen
    left: '8rem', // Maintain position on normal screens
    width: 'calc(100% - 16rem)', // Adjust width dynamically based on left margin
    display: 'flex',
    flexDirection: 'row', // Keep buttons in a row
    justifyContent: 'space-between', // Align Back to the left and Next to the right
    alignItems: 'center',
    padding: '0', // Remove extra padding
    gap: '0', // Remove gaps between buttons
    '@media (max-width: 768px)': {
      left: '1rem', // Adjust left margin for smaller screens
      width: 'calc(100% - 2rem)', // Adjust width for smaller screens
    },
  }}
>
  <Button
    variant="contained"
    style={{
      backgroundColor: '#F6EB39',
      color: '#2E2E38',
      fontFamily: 'Helvetica Neue',
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '22px',
      textAlign: 'center',
    }}
    onClick={handleBack}
  >
    Back
  </Button>
  <Button
    variant="contained"
    style={{
      backgroundColor: '#F6EB39',
      color: '#2E2E38',
      fontFamily: 'Helvetica Neue',
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '22px',
      textAlign: 'center',
    }}
    onClick={onNext}
  >
    Next
  </Button>
</Box>
    </div>
  </div>
  );
};

export default RequestersInformation;